import { useState, useCallback, useEffect } from "react";

import { useAuth } from "@clerk/clerk-react";

import { fnUseSupabaseToken } from "../auth/auth";
import { appJotaiStore, useAtom, useAtomValue } from "../app-jotai";
import {
  bCloudSaveDialogOpenAtom,
  bSaveInProgressAtom,
  sActiveDrawingIdAtom,
  sActiveDrawingNameAtom,
  sActiveFolderIdAtom,
  excalidrawAPIAtom,
} from "../cloud/cloudState";
import { fnSaveDrawing, fnListFolders } from "../cloud/cloudStorage";

import type { iFolderMetadata } from "../cloud/types";

import "./CloudSaveDialog.scss";

/**
 * Dialog for naming a new cloud drawing or "Save As" with a new name.
 * Opened when user saves and has no active cloud drawing ID.
 */
export const CloudSaveDialog = () => {
  const { isSignedIn } = useAuth();
  const [bIsOpen, fnSetIsOpen] = useAtom(bCloudSaveDialogOpenAtom);
  const bSaving = useAtomValue(bSaveInProgressAtom);
  const oExcalidrawAPI = useAtomValue(excalidrawAPIAtom);
  const fnGetToken = fnUseSupabaseToken();

  const sActiveName = useAtomValue(sActiveDrawingNameAtom);
  const sActiveFolderId = useAtomValue(sActiveFolderIdAtom);
  const [sName, fnSetName] = useState(sActiveName || "");
  const [sFolderId, fnSetFolderId] = useState<string | null>(sActiveFolderId);
  const [aFolders, fnSetFolders] = useState<iFolderMetadata[]>([]);
  const [bFolderError, fnSetFolderError] = useState(false);

  useEffect(() => {
    if (!bIsOpen) {
      return;
    }
    fnSetName(sActiveName || "");
    fnSetFolderId(sActiveFolderId);

    const fnLoad = async () => {
      const sToken = await fnGetToken();
      if (!sToken) {
        return;
      }
      const oResult = await fnListFolders(sToken);
      if (oResult.bError) {
        fnSetFolderError(true);
        fnSetFolders([]);
      } else {
        fnSetFolderError(false);
        fnSetFolders(oResult.aFolders);
      }
    };
    fnLoad();
  }, [bIsOpen, sActiveName, sActiveFolderId, fnGetToken]);

  const fnHandleSave = useCallback(async () => {
    if (!oExcalidrawAPI || !sName.trim()) {
      return;
    }

    appJotaiStore.set(bSaveInProgressAtom, true);

    try {
      const sToken = await fnGetToken();
      if (!sToken) {
        oExcalidrawAPI.setToast({
          message: "Not signed in. Please sign in to save.",
        });
        return;
      }

      const aElements = oExcalidrawAPI.getSceneElements();
      const oAppState = oExcalidrawAPI.getAppState();
      const oFiles = oExcalidrawAPI.getFiles();

      const oResult = await fnSaveDrawing(
        aElements,
        oAppState,
        oFiles,
        sName.trim(),
        sToken,
        undefined,
        sFolderId,
      );

      if (oResult.bSuccess && oResult.sDrawingId) {
        appJotaiStore.set(sActiveDrawingIdAtom, oResult.sDrawingId);
        appJotaiStore.set(sActiveDrawingNameAtom, sName.trim());
        oExcalidrawAPI.setToast({ message: "Saved to cloud" });
        fnSetIsOpen(false);
      } else {
        oExcalidrawAPI.setToast({
          message: "Failed to save. Please try again.",
        });
      }
    } catch (error) {
      console.error("Cloud save dialog error:", error);
      oExcalidrawAPI.setToast({ message: "Save failed unexpectedly." });
    } finally {
      appJotaiStore.set(bSaveInProgressAtom, false);
    }
  }, [oExcalidrawAPI, sName, sFolderId, fnGetToken, fnSetIsOpen]);

  if (!bIsOpen || !isSignedIn) {
    return null;
  }

  return (
    <div
      className="cloud-save-dialog-overlay"
      onClick={() => fnSetIsOpen(false)}
    >
      <div className="cloud-save-dialog" onClick={(e) => e.stopPropagation()}>
        <h3 className="cloud-save-dialog__title">Save to Cloud</h3>
        <input
          className="cloud-save-dialog__input"
          type="text"
          placeholder="Drawing name"
          maxLength={100}
          value={sName}
          onChange={(e) => fnSetName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !bSaving) {
              fnHandleSave();
            }
          }}
          autoFocus
        />

        {/* Folder picker */}
        <div className="cloud-save-dialog__folder-picker">
          <label className="cloud-save-dialog__label">Folder</label>
          {bFolderError ? (
            <span className="cloud-save-dialog__folder-error">
              Could not load folders
            </span>
          ) : (
            <select
              className="cloud-save-dialog__select"
              value={sFolderId ?? ""}
              onChange={(e) =>
                fnSetFolderId(e.target.value === "" ? null : e.target.value)
              }
            >
              <option value="">
                {aFolders.length === 0 ? "No folders yet" : "No folder"}
              </option>
              {aFolders.map((oFolder) => (
                <option key={oFolder.id} value={oFolder.id}>
                  {oFolder.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="cloud-save-dialog__actions">
          <button
            className="cloud-save-dialog__btn cloud-save-dialog__btn--cancel"
            onClick={() => fnSetIsOpen(false)}
          >
            Cancel
          </button>
          <button
            className="cloud-save-dialog__btn cloud-save-dialog__btn--save"
            onClick={fnHandleSave}
            disabled={bSaving || !sName.trim()}
          >
            {bSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};
